<template>
  <div class="sidebar-order__container">
    <section class="sidebar-order__balance">
      <AvailableBalanceV2
        class="sidebar-order__balance__value"
        :available-balance-data="availableBalanceData"
        :loading="balanceDataStatus === BalanceDataStatus.Loading"
        :error="balanceDataStatus === BalanceDataStatus.Error"
        @on-retry="fetchAvailableBalance"
      />
    </section>
    <InputNumber
      v-model.number="orderQuantity"
      label="Quantidade"
      :helper-text="error"
      :status-type="orderStatusType"
      :increment-by="instrumentLotSize"
      :min-value="1"
      autocomplete="off"
      data-testid="order-quantity"
    />
    <p v-if="alert" class="text-p-6 order-input__alert">
      {{ alert }}
      <router-link
        v-if="showAlertLink"
        :to="toInstrumentView"
        class="order-input__alert-link"
      >
        <span class="order-input__alert-action">{{ actionOrder }}</span>
        {{ actionLabel }}
        <BaseIcon
          icon="ic_chevron_gray"
          class="order-input__alert-chevron"
          width="8px"
          height="8px"
          :colors="NebraskaColors.tradePrimary"
        />
      </router-link>
    </p>
    <div>
      <ListDisplay
        class="order-price-unified-account__offer"
        title="Melhor oferta"
        :value="displayValue(priceMarket)"
      />
      <ListDisplay
        class="order-price-unified-account__estimated"
        title="Total estimado"
        :value="displayValue(priceTotal)"
      />
    </div>
    <AvailableBalance
      v-if="balanceDataStatus === BalanceDataStatus.Defaut"
      :balance-available="currentBalanceAvailable"
      :quantity-available="quantityInstrument"
      :order-type="type"
      is-row
      title-level="5"
      balance-level="5"
    />
    <div class="trade-order__inputs">
      <div v-if="shouldEnableMFA" class="trade-mfa-content">
        <p class="text-p-6">Ative a verificação em duas etapas para negociar</p>
        <ButtonPrimary
          label="Ativar"
          :disabled="isImpersonateFlag"
          @click="goToMFAPage"
        />
      </div>
      <ButtonPrimary
        v-else
        class="trade-order__inputs__button"
        label="Continuar"
        :loading="loadingButton"
        :disabled="isButtonDisabled"
        data-testid="create-order-button"
        @click="confirmTypeOrder"
      />
    </div>
    <p
      v-if="instrumentInAuction"
      class="sidebar-intrument-status-text text-p-5"
    >
      {{ instrumentInAuctionMessage }}
    </p>
    <p v-if="!shouldEnableMFA" class="sidebar-order__avaliable-text text-p-6">
      {{ orderMessage }}
    </p>
    <BaseText
      :color="NebraskaColors.textSecondary"
      size="sm"
      class="sidebar-order__delay-text"
    >
      Toda transmissão de ordem pela internet está sujeita a interrupções ou
      atrasos, podendo impedir ou prejudicar o envio de ordens ou a recepção de
      informações atualizadas.
    </BaseText>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Watch, Emit } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';

import { RouteMixin } from '@/modules/common/mixins/route';
import { OrderInput } from './components/OrderInput';
import {
  openRiskTermsModal,
  RiskTermsConfirmation,
  RiskTermsAnswers
} from '@/modules/trade/components/InstrumentRiskModal';
import { moneyViewer as money } from '@/modules/common/helpers/currency';
import { percentageWithSign as percentage } from '@/modules/common/helpers/percentage';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseIcon,
  Row,
  BaseText,
  ListDisplay,
  InputNumber,
  ButtonPrimary,
  SkeletonLoader
} from '@warrenbrasil/nebraska-web';
import { isImpersonate } from '@/modules/common/services/partner';
import { AvailableBalance } from './components/AvailableBalance';
import { OrderTypesSecondary, OrderType, OrderErrors } from './types';
import { ETradeTypeInstrument } from '@/modules/trade/services/instruments/types';
import { IInstrumentStatus } from '@/modules/trade/services/signalr';
import {
  trackOpenRiskModal,
  trackOrderOperationClick
} from '@/modules/trade/views/Instrument/trackers';
import { AvailableBalanceData } from '@/modules/trade/services/unified-account/types';
import { getUserAvailableBalance } from '@/modules/trade/services/unified-account';
import { WarrenModules } from '@/types/navigation';
import { MFAModules } from '@/modules/authentication/views/SecurityView/components/MultiFactorAuthentication';
import { AvailableBalance as AvailableBalanceV2 } from '@/modules/trade/components/AvailableBalance';
import { AccountStatus } from '@/modules/trade/services/account';
import { InstrumentStatus } from '@/modules/trade/services/instruments';
import { getDomainAndPlatform } from '@/modules/common/helpers/onboarding/getDomainAndPlatform';
import { namespace } from 'vuex-class';
const tradeModule = namespace('tradeModule');

enum BalanceDataStatus {
  Loading = 'Loading',
  Error = 'Error',
  Defaut = 'Defaut'
}

@Component({
  filters: { money, percentage },
  components: {
    OrderInput,
    Row,
    BaseIcon,
    AvailableBalance,
    SkeletonLoader,
    BaseText,
    InputNumber,
    ButtonPrimary,
    ListDisplay,
    AvailableBalanceV2
  }
})
export default class Order extends mixins(RouteMixin) {
  balanceDataStatus = BalanceDataStatus.Defaut;
  availableBalanceData: AvailableBalanceData | null = null;

  @Prop({ type: Number, required: true })
  readonly priceMarket!: number;

  @Prop({ type: Number, required: true })
  readonly lotSize!: number;

  @Prop({ type: String, required: true })
  readonly instrumentTitle!: string;

  @Prop({ type: Number, required: true, default: OrderType.Buy })
  readonly type!: OrderType;

  @Prop({ type: Number, default: 0 })
  readonly quantityInstrument!: number;

  @Prop({ type: Boolean, required: false })
  readonly canOperate!: boolean;

  @Prop({ type: Object })
  readonly instrumentStatus!: IInstrumentStatus;

  @Prop({ type: Object, required: true })
  readonly status!: AccountStatus;

  @Prop({ type: Object, required: true })
  readonly instrument!: InstrumentStatus;

  @Prop({ type: Boolean, default: false })
  readonly isCustomerBlocked!: boolean;

  @Prop({ type: String, default: '' })
  readonly description!: string;

  @Prop({ type: Boolean, default: false })
  readonly loadingButton!: boolean;

  @tradeModule.State('lotSize')
  readonly oldLotSize!: number;

  @tradeModule.Mutation('setLotSize')
  private setLotSize!: Function;

  orderQuantity = 0;
  private actionLabel = this.instrumentTitle;
  readonly NebraskaColors = NebraskaColors;
  private alert: string | null = null;

  error: string | null = null;

  @Emit('on-accept-risk-terms')
  emitAcceptRiskTerms() {}

  @Emit('on-order-button-disabled')
  emitOnOrderButtonDisabled(_reason: string) {}

  get BalanceDataStatus() {
    return BalanceDataStatus;
  }

  get orderStatusType() {
    return this.error ? 'error' : 'default';
  }

  get balanceAvailableUnifiedAccountFormatted() {
    return this.availableBalanceData?.availableToOperateValue?.formatted;
  }

  get currentBalanceAvailable() {
    return this.availableBalanceData?.availableToOperateValue;
  }

  private get isImpersonateFlag() {
    return isImpersonate();
  }

  get instrumentLotSize() {
    return this.instrument.lotSize;
  }

  get isLot() {
    return this.instrument.lotSize !== 1;
  }

  async created() {
    this.orderQuantity = this.instrumentLotSize;
    this.fetchAvailableBalance();
  }

  private get actionOrder() {
    return this.type === OrderType.Buy
      ? OrderTypesSecondary.Buy
      : OrderTypesSecondary.Sell;
  }

  get showAlertLink() {
    return (
      this.alert !==
      `A quantidade para lotes padrão deve ser múltipla de ${this.instrumentLotSize}.`
    );
  }

  get toInstrumentView() {
    this.instrumentLotSize !== 1 && this.setLotSize(this.instrumentLotSize);
    return { name: 'tradeInstrument', params: { ticker: this.actionLabel } };
  }

  async fetchAvailableBalance() {
    this.balanceDataStatus = BalanceDataStatus.Loading;
    try {
      this.availableBalanceData = await getUserAvailableBalance();
      this.balanceDataStatus = BalanceDataStatus.Defaut;
      if (this.type === OrderType.Buy) this.validateBuy();
      this.onLoadAvailableBalanceUnifiedAccount();
    } catch {
      this.balanceDataStatus = BalanceDataStatus.Error;
    }
  }

  @Emit('load-available-balance-unified-account')
  onLoadAvailableBalanceUnifiedAccount() {
    return this.availableBalanceData;
  }

  private typeOfOrderDisabled() {
    return this.type === OrderType.Buy
      ? this.isBuyDisabled()
      : this.isSellDisabled();
  }

  private isSellDisabled() {
    const emptyOrder = this.orderQuantity <= 0;
    if (emptyOrder || this.error) {
      return true;
    }
    return false;
  }

  private isBuyDisabled() {
    const emptyOrder = this.orderQuantity <= 0;
    const emptyBalanceAvailable =
      this.currentBalanceAvailable && this.currentBalanceAvailable.value === 0;
    if (emptyBalanceAvailable || emptyOrder || this.error) {
      return true;
    }
    return false;
  }

  getReasonToDisableButton() {
    if (!this.status.tradingAvailable) {
      return 'tradingAvailable';
    } else if (this.isImpersonateFlag) {
      return 'isImpersonateFlag';
    } else if (!this.status.marketAvailable?.isMarketAvailable) {
      return 'isMarketAvailable';
    } else if (this.alert) {
      return 'alert';
    } else if (this.error) {
      return 'error';
    } else if (this.isSubscriptionInstrument) {
      return 'isSubscriptionInstrument';
    } else if (this.instrumentInAuction) {
      return `instrumentInAuction (value: ${this.instrumentStatus?.value}, message: ${this.instrumentStatus?.message})`;
    } else if (this.typeOfOrderDisabled()) {
      return 'typeOfOrderDisabled';
    }

    return null;
  }

  private get isButtonDisabled() {
    const reasonToDisabledButton = this.getReasonToDisableButton();

    if (reasonToDisabledButton) {
      this.emitOnOrderButtonDisabled(reasonToDisabledButton);
      return true;
    }
    return false;
  }

  private async confirmTypeOrder() {
    if (this.isCustomerBlocked) {
      this.$router.push({
        name: 'regulatory-update',
        query: { cameFrom: WarrenModules.Trade }
      });
      return;
    }

    const orderValue = (this.orderQuantity * this.priceMarket) / this.lotSize;
    trackOrderOperationClick(
      !this.isLot,
      this.type,
      orderValue,
      this.instrument.symbol,
      this.description,
      this.instrument.type!.formatted!
    );

    if (this.type === OrderType.Buy) {
      await this.buyConfirm();
    } else {
      this.sellConfirm();
    }
  }

  private get shouldEnableMFA() {
    return !this.status.enabledMFA;
  }

  get negotiationAvailable() {
    return this.status.marketAvailable!.negotiationAvailable!;
  }

  private get orderMessage() {
    return this.status?.marketAvailable?.innerDescription;
  }

  private goToMFAPage() {
    this.$router.push({
      name: 'security',
      query: {
        module: MFAModules.TRADE,
        ticker: this.instrumentTitle
      }
    });
  }

  public goToSuitability() {
    const { domain, platform } = getDomainAndPlatform();
    const backlink = window.location.hash.replace('#/', '');

    window.location.href = `/onboarding/${domain}/${platform}/suitability?backlink=/${backlink}`;
  }

  private async requestAcceptRiskTerms() {
    trackOpenRiskModal(this.instrument.symbol);

    const { answer } = (await openRiskTermsModal({
      symbol: this.instrument.symbol,
      instrumentType: this.instrument.type!.value!,
      riskProfile: this.instrument.riskProfile!,
      orderType: this.type === OrderType.Buy ? 'buy' : 'sell'
    })) as RiskTermsConfirmation;

    switch (answer) {
      case RiskTermsAnswers.REDO_SUITABILITY:
        this.goToSuitability();
        return false;
      case RiskTermsAnswers.ACCEPT_RISK_TERMS:
        this.emitAcceptRiskTerms();
        return true;
      case RiskTermsAnswers.CLOSE_RISK_TERMS:
        return false;
    }
  }

  private validateBuy() {
    if (this.balanceDataStatus === BalanceDataStatus.Defaut) {
      if (
        typeof this.currentBalanceAvailable?.value !== 'undefined' &&
        this.priceTotal > this.currentBalanceAvailable.value
      ) {
        this.error = OrderErrors.BuyMessage;
        return false;
      }
    }

    return true;
  }

  private async buyConfirm() {
    const isEnableToBuy = this.validateBuy();

    if (!this.canOperate) {
      const hasAcceptedRiskTerms = await this.requestAcceptRiskTerms();
      if (!hasAcceptedRiskTerms) return;
    }

    if (isEnableToBuy) {
      this.$emit('confirm-order', this.orderQuantity, this.priceMarket);
    }
  }

  private validateSell() {
    if (this.orderQuantity > this.quantityInstrument) {
      this.error = OrderErrors.SellMessage;
      return false;
    }
    return true;
  }

  private async sellConfirm() {
    if (!this.canOperate) {
      const hasAcceptedRiskTerms = await this.requestAcceptRiskTerms();
      if (!hasAcceptedRiskTerms) return;
    }

    this.$emit('confirm-order', this.orderQuantity, this.priceMarket);
  }

  private validateStock() {
    const isStockType =
      this.instrument.type?.value === ETradeTypeInstrument.Stock;

    if (this.isLot) {
      if (this.orderQuantity < this.instrumentLotSize) {
        this.alert = `Para quantidades menores que ${this.instrumentLotSize}, negocie ações fracionadas.`;
        this.setToFractionary();
        return false;
      }
      if (this.orderQuantity % this.instrumentLotSize !== 0) {
        this.alert = `A quantidade para lotes padrão deve ser múltipla de ${this.instrumentLotSize}.`;
        return false;
      }
    }
    if (
      isStockType &&
      !this.isLot &&
      this.orderQuantity > this.oldLotSize - 1
    ) {
      this.alert = `Para quantidades maiores que ${
        this.oldLotSize - 1
      }, negocie lotes padrão.`;
      this.setToStandard();
      return false;
    }
    return true;
  }

  private validateOrderQuantity() {
    if (this.type === OrderType.Sell && this.validateSell()) {
      this.error = null;
    }
    if (this.type === OrderType.Buy && this.validateBuy()) {
      this.error = null;
    }
  }

  @Watch('orderQuantity')
  checkOrderErrorsAfterChangeQuantity() {
    if (this.validateStock()) {
      this.alert = null;
    }
    this.validateOrderQuantity();
    if (
      this.error === OrderErrors.BuyMessage &&
      this.priceTotal <= this.currentBalanceAvailable?.value!
    ) {
      this.error = null;
    }
  }

  private get priceTotal() {
    return (this.priceMarket * this.orderQuantity) / this.lotSize;
  }

  private setToStandard() {
    const instrument = this.instrumentTitle;
    this.actionLabel = this.isLot
      ? instrument
      : instrument.substring(0, instrument.length - 1);
  }

  private setToFractionary() {
    this.actionLabel = `${this.instrumentTitle}F`;
  }

  private displayValue(value: number) {
    return money(value);
  }

  private get instrumentInAuction() {
    return this.instrumentStatus && this.instrumentStatus.message;
  }

  private get instrumentInAuctionMessage() {
    return this.instrumentStatus.message;
  }

  private get isSubscriptionInstrument() {
    return (
      this.instrument.type!.value === ETradeTypeInstrument.SubscriptionReceipt
    );
  }
}
</script>

<style lang="less" scoped>
.sidebar-order {
  &__balance {
    margin-bottom: @size-spacing-x450;

    &__value {
      width: 100%;
      padding-bottom: @size-spacing-x350;
      border-bottom: @size-border-x400 solid @divider-primary;
    }
  }
}

.order-input {
  padding: 0 @size-spacing-x350;
  background: @background-primary;
  border-radius: 4px;
  display: flex;
  align-items: center;
  border: 1px solid transparent;
  height: @size-spacing-x900;
}

.balance {
  margin-top: 21px;
  color: @text-secondary;
  user-select: none;
}

.order-input_icons {
  display: inline-block;

  .input-icons_span {
    background: @background-hover;
    border-radius: 16px;
    cursor: pointer;
    margin: @size-spacing-x150 0 0 @size-spacing-x350;
    width: @size-spacing-x600;
    height: @size-spacing-x600;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.order-input__alert {
  color: @text-secondary;
  margin-top: @size-spacing-x150;
  user-select: none;
}

.order-input__alert-link {
  color: @trade-primary;
  text-decoration: underline;
  font-weight: bold;
}

.order-input__alert-chevron {
  margin-left: @size-spacing-x150;
}

.order-input__alert-action {
  text-transform: capitalize;
}

.order-input_error {
  color: @text-on-error;
  margin-top: @size-spacing-x150;
  user-select: none;
}

.order-qtd_text {
  color: @text-primary;
  user-select: none;
}
.order-price-unified-account {
  &__estimated {
    margin-top: @size-spacing-x300;
    margin-bottom: @size-spacing-x500;
  }

  &__offer {
    margin-top: @size-spacing-x600;
  }
}

.order-price {
  padding: 11px 0 11px 0;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid @divider-primary;
  border-bottom: 1px solid @divider-primary;

  p {
    font-size: 20px;
    color: @text-primary;
    user-select: none;
  }
}

.trade-order__inputs {
  margin-top: @size-spacing-x400;
  .trade-order__inputs__button {
    width: 100%;
  }
}

.order-price_market {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: @size-spacing-x350 0;
  p {
    user-select: none;
  }
}

.order-input_qtd {
  outline: none;
  border: none;
  background-color: transparent;
  margin: 0;
  padding: 0;
  width: auto;
}

.order-input_qtd::-moz-focus-inner {
  border: 0;
}

.trade-mfa-content {
  display: flex;
  align-items: center;

  button {
    margin-left: @size-spacing-x600;
  }
}

.order-price__best_offer {
  color: @text-secondary;
}

/deep/ .order-inputs__container {
  background-color: @background-primary;
  margin: @size-spacing-x150 0 0 0;
}

.sidebar-order__container {
  padding: @size-spacing-x600 @size-spacing-x400 @size-spacing-x500
    @size-spacing-x400;
}

.sidebar-order__avaliable-text {
  padding-top: @size-spacing-x500;
  text-align: center;
}

.sidebar-intrument-status-text {
  color: @text-secondary;
  padding-top: @size-spacing-x500;
  text-align: center;
}

.sidebar-order__delay-text {
  padding-top: @size-spacing-x500;
  text-align: left;
}

.modal-wrapper {
  padding: 3rem 8rem;

  @media @mobile {
    padding: 1rem 4rem;
  }
}

@media @notebookUp {
  .sidebar-order__container {
    padding: @size-spacing-x600 @size-spacing-x800 @size-spacing-x500
      @size-spacing-x800;
  }
}
</style>
